import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import Page from "components/page/Page";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select/index";
import { handleChangeEventInput, unsavedChangesExist } from "helpers/Helpers";
import Input from "components/form/input/Input";
import { ButtonNew as Button } from "components/buttonNew";
import axios, { CancelToken } from "axios";
import { IAccommodationBuilding } from "../buildings/Buildings";
import { IAccommodationUnitType } from "../unitTypes/UnitTypes";
import {
  IPutAccommodationUnit,
  PutAccommodationUnit,
  GetAccommodationUnits,
} from "api/rpc/2024-04/facilityAdmin/accommodation/units";
import { GetAccommodationUnitTypes } from "api/rpc/2024-04/facilityAdmin/accommodation/unitTypes";
import { GetAccommodationBuildings } from "api/rpc/2024-04/facilityAdmin/accommodation/building";

export function EditUnit() {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { unitId }: any = useParams();

  const [editUnit, setEditUnit] = useState<IPutAccommodationUnit>({
    id: undefined,
    title: "",
    building_id: undefined,
    unit_type_id: undefined,
    code: "",
    floor: "",
    status: "active",
  });

  const [editUnitBeforeChanges, setEditUnitBeforeChanges] = useState<IPutAccommodationUnit>(undefined);

  const [accommodationUnitTypes, setAccommodationUnitTypes] = useState(undefined);
  const [accommodationBuildings, setAccommodationBuildings] = useState(undefined);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAccommodationUnit();
    void loadAccommodationBuildings();
    void loadAccommodationUnitTypes();
    return () => {
      source.cancel();
    };
  }, []);

  async function loadAccommodationUnit() {
    if (unitId !== undefined) {
      setEditUnit(undefined);
    }
    const accommodationUnitRes = await GetAccommodationUnits({ id: unitId }, false);

    if (accommodationUnitRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting accommodation unit")));
      return;
    }

    const accommodationUnit = accommodationUnitRes?.data?.[0];

    setEditUnit(prevState => ({
      ...prevState,
      id: accommodationUnit?.id,
      title: accommodationUnit?.title,
      building_id: accommodationUnit?.building_id,
      unit_type_id: accommodationUnit?.unit_type_id,
      code: accommodationUnit?.code,
      floor: accommodationUnit?.floor ?? "",
      status: accommodationUnit?.status,
    }));

    setEditUnitBeforeChanges(prevState => ({
      ...prevState,
      id: accommodationUnit?.id,
      title: accommodationUnit?.title,
      building_id: accommodationUnit?.building_id,
      unit_type_id: accommodationUnit?.unit_type_id,
      code: accommodationUnit?.code,
      floor: accommodationUnit?.floor ?? "",
      status: accommodationUnit?.status,
    }));
  }

  async function loadAccommodationUnitTypes(token?: CancelToken) {
    if (accommodationUnitTypes !== undefined) {
      setAccommodationUnitTypes(undefined);
    }
    const accommodationUnitTypesRes = await GetAccommodationUnitTypes(null, false);

    if (token && token.reason) {
      return;
    }
    if (accommodationUnitTypesRes.status !== StatusCode.OK) {
      setAccommodationUnitTypes([]);
      return;
    }

    setAccommodationUnitTypes(accommodationUnitTypesRes.data);
  }

  async function loadAccommodationBuildings(token?: CancelToken) {
    if (accommodationBuildings !== undefined) {
      setAccommodationBuildings(undefined);
    }
    const accommodationBuildingsRes = await GetAccommodationBuildings(null, false);

    if (token && token.reason) {
      return;
    }
    if (accommodationBuildingsRes.status !== StatusCode.OK) {
      setAccommodationBuildings([]);
      return;
    }

    setAccommodationBuildings(accommodationBuildingsRes.data);
  }

  const handleSaveEdit = async () => {
    try {
      const unitRes = await PutAccommodationUnit(
        {
          id: editUnit.id,
          title: editUnit.title,
          building_id: editUnit.building_id,
          unit_type_id: editUnit.unit_type_id,
          floor: editUnit.floor,
          code: editUnit.code,
          status: editUnit.status,
        },
        true,
      );

      if (unitRes.status !== StatusCode.OK) {
        dispatch(showError("Error saving unit"));
        return;
      }

      setEditUnitBeforeChanges(editUnit);

      dispatch(showSuccess(t("Successfully saved unit")));
    } catch (error) {
      console.error(error);
      dispatch(showError("Error saving unit"));
    }
  };

  return (
    <Page
      title={t("Edit Unit")}
      subtitle={t("Enter the key details for your unit")}
      notificationBarProps={{
        isVisible: unsavedChangesExist(editUnit, editUnitBeforeChanges),
        onAction: () => handleSaveEdit(),
        onCancel: () => setEditUnit({ ...editUnitBeforeChanges }),
      }}
      breadcrumbs={[
        { label: t("Accommodations"), url: "/admin/accommodation" },
        { label: t("Property Configuration"), url: "/admin/accommodation/configuration/units" },
        { label: t("Units"), url: "/admin/accommodation/configuration/units" },
      ]}
    >
      <Form>
        <Card title="Unit details">
          <div style={{ paddingLeft: "24px", paddingRight: "24px" }}>
            <hr style={{ marginBottom: "0px" }} />
          </div>

          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={editUnit?.title}
                  label={t("Name of unit")}
                  id="title"
                  onChange={e => handleChangeEventInput(e, editUnit, setEditUnit)}
                  placeholder={t("Enter unit name")}
                />
                <Input
                  value={editUnit?.floor}
                  label={t("Floor")}
                  id="floor"
                  onChange={e => handleChangeEventInput(e, editUnit, setEditUnit)}
                  placeholder={t("Enter floor")}
                  type={"text"}
                />

                <Input
                  value={editUnit?.code}
                  label={t("Code")}
                  id="code"
                  onChange={e => handleChangeEventInput(e, editUnit, setEditUnit)}
                  placeholder={"Enter code"}
                  type={"text"}
                />
              </FormLayout.Group>

              <FormLayout.Group>
                <Select
                  label={t("Building")}
                  onChange={(value: number) => setEditUnit(prev => ({ ...prev, building_id: Number(value) }))}
                  defaultValue={editUnit?.building_id}
                  placeholder="Select building"
                >
                  {accommodationBuildings?.map((building: IAccommodationBuilding) => (
                    <Select.Option key={building.id} value={building.id} name={building.title}>
                      {building.title}
                    </Select.Option>
                  ))}
                </Select>

                <Select
                  label={t("Unit type")}
                  onChange={(value: number) => setEditUnit(prev => ({ ...prev, unit_type_id: Number(value) }))}
                  defaultValue={editUnit?.unit_type_id}
                  placeholder="Select unit type"
                >
                  {accommodationUnitTypes?.map((unitType: IAccommodationUnitType) => (
                    <Select.Option key={unitType.id} value={unitType.id} name={unitType.title}>
                      {unitType.title}
                    </Select.Option>
                  ))}
                </Select>
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        </Card>

        {/* <FormLayout>
          <FormLayout.Group>
            <div></div>
            <div></div>
            <div className="flex justify-end mt-3">
              <Button type="secondary" onClick={() => history.push("/admin/accommodation/configuration/units")}>
                {"Cancel"}
              </Button>

              <Button type="primary" onClick={() => handleSaveEdit()}>
                {"Save Changes"}
              </Button>
            </div>
          </FormLayout.Group>
        </FormLayout> */}
      </Form>
    </Page>
  );
}
